import styled from '@emotion/styled'
import * as ToggleGroup from '@radix-ui/react-toggle-group'

import { Color, theme } from '../../../constants'
import { SpacingValue } from '../../../utils'
import { Flex } from '../../Flex'
import { Label } from '../../Form/Label'
import { Icon, IconName } from '../../Icon'

type Option<T> = {
  label?: string
  icon?: IconName
  value: T
  hidden?: boolean
}

export type ButtonGroupProps<T> = {
  options: Option<T>[]
  selectedOption?: T
  onClick: (newSelection: T) => void
  thin?: boolean
  short?: boolean
  label?: string
  my?: SpacingValue
  ml?: SpacingValue | 'auto'
}

const StyledToggleGroup = styled(ToggleGroup.Root)`
  display: inline-grid;
  border: 1px solid ${Color.DARK_MEDIUM_GREY};
  border-radius: 6px;
  overflow: hidden;
  height: fit-content;
  width: fit-content;
  box-shadow: ${theme.boxShadow.normal};
`

type StyledToggleItemProps = {
  thin: boolean
  short: boolean
}

const StyledToggleItem = styled(ToggleGroup.Item)<StyledToggleItemProps>`
  padding: ${({ short }) => (short ? 4 : 6)}px
    ${({ thin }) => (thin ? 6 : 16)}px;
  font-size: ${({ short }) => (short ? 14 : 16)}px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 6px;
  color: ${Color.BLACK};
  background: ${Color.WHITE};
  transition: all 0.3s ease-out;
  cursor: pointer;
  border: none;

  &:hover {
    background: ${Color.LIGHTEST_BLUE};
  }

  &:not(:first-of-type) {
    border-left: 1px solid ${Color.DARK_MEDIUM_GREY};
  }

  &[data-state='on'] {
    color: ${Color.WHITE};
    background: ${Color.BLUE};

    &:hover {
      background: ${Color.DARK_BLUE};
    }
  }

  user-select: none;
`

export function ButtonGroup<T extends string | number | boolean>({
  options,
  selectedOption,
  onClick,
  thin = false,
  short = false,
  label,
  my,
  ml,
}: ButtonGroupProps<T>): JSX.Element {
  const visibleOptions = options.filter((option) => !option.hidden)

  const handleValueChange = (value: string) => {
    // Convert the value back to the original type
    let typedValue: T

    if (typeof selectedOption === 'boolean') {
      typedValue = (value === 'true') as unknown as T
    } else if (typeof selectedOption === 'number') {
      typedValue = Number(value) as unknown as T
    } else {
      typedValue = value as unknown as T
    }

    onClick(typedValue)
  }

  const buttonGroup = (
    <StyledToggleGroup
      type="single"
      value={String(selectedOption)}
      onValueChange={handleValueChange}
      style={{
        gridTemplateColumns: `${'1fr '.repeat(visibleOptions.length).trim()}`,
      }}
    >
      {visibleOptions.map((option) => (
        <StyledToggleItem
          thin={thin}
          short={short}
          key={`${option.value}`}
          value={String(option.value)}
        >
          {option.icon && <Icon my={0.5} name={option.icon} size="sm" />}
          {option.label && <span>{option.label}</span>}
        </StyledToggleItem>
      ))}
    </StyledToggleGroup>
  )

  return (
    <Flex col styles={{ my, ml }}>
      {label ? <Label text={label}>{buttonGroup}</Label> : buttonGroup}
    </Flex>
  )
}
